import {useDispatch, useSelector} from "react-redux";
import {getEditorTitle, isEditorDetached} from "components/store/application.reducers";
import {editorReattach} from "components/taskdetails-remake/taskDetails.action";
import NewWindow from "components/taskdetails-remake/panels/attachments/detached/NewWindow";
import {productWindowTitle} from "utils/productName.function";
import type {DocumentEditorWindowProps} from "types/editor";
import DocumentEditorPanel from "../DocumentEditorPanel.component";
import AccountingGridPanel from "../AccountingGridPanel.component";
import "../../../style/editor.css";

const DEFAULT_WIDTH = 1200;
const DEFAULT_HEIGHT = 800;

/**
 * The window the editor modules are detached into: the external editor and the accounting grid, full
 * width one over the other, without the buttons that place them in the page. Closing it reattaches them.
 * It replaces taskdetails-remake/panels/editor/detached/DocumentEditorWindow.component on the redesigned page.
 */
export function DocumentEditorWindow(props: DocumentEditorWindowProps) {
    const {onKeyPress} = props;
    const dispatch = useDispatch();
    const isDetached: boolean = useSelector(isEditorDetached);
    const editorTitle: string | undefined = useSelector(getEditorTitle);
    const width = Number.parseInt(window.localStorage.getItem("@@grid/editor-width") ?? "") || DEFAULT_WIDTH;
    const height = document?.body.clientHeight || DEFAULT_HEIGHT;

    function onOpen() {
        window.onbeforeunload = onUnload;
    }

    function onUnload() {
        dispatch(editorReattach());
        window.onbeforeunload = null;
    }

    if (!isDetached) {
        return null;
    }

    const externalEditorAvailable = props.taskDetails?.externalEditorAvailable;
    const accountingGridVisible = props.task?.hasAccountingGrid || props.task?.allowGridEdit || props.taskDetails?.allowGridEdit;
    const hasContent = externalEditorAvailable || accountingGridVisible;

    return (
        <div>
            <NewWindow features={{width: width, height: height, location: false}}
                       title={editorTitle || productWindowTitle()}
                       name={"DocumentEditorWindow"}
                       onOpen={onOpen}
                       onKeyPress={onKeyPress}
                       onUnload={onUnload}>
                <div className="editor-window">
                    {externalEditorAvailable &&
                        <DocumentEditorPanel {...props}
                                             renderInPortal={false}
                                             inDetachedWindow={true}/>
                    }
                    {accountingGridVisible &&
                        <AccountingGridPanel {...props}
                                             renderInPortal={false}
                                             inDetachedWindow={true}/>
                    }
                    {!hasContent &&
                        <div className="editor-window__empty">
                            {props.translate("taskDetail.noEditorAvailable")}
                        </div>
                    }
                </div>
            </NewWindow>
        </div>
    );
}

export default DocumentEditorWindow;
